/**
 * LA TARJETA DE VENTAS — "¿el bot me trae ventas?", en el dashboard.
 *
 * Lee de `metrics/ecommerce/daily` y de nada más: una sola fuente para los tres
 * rangos. Todo el cálculo vive en `utils/ventasResumen`, incluida la conversión,
 * que se calcula POR MONTO y no se lee del campo `conversionRate` del documento
 * —que es por pedidos y da distinto—.
 *
 * NUNCA MUESTRA "$0" A SECAS. Mientras el agregador no haya corrido no hay
 * documento, y un cero ahí se lee igual que "no vendiste". Los dos casos se
 * dicen distinto, y el primero se apaga solo con la primera corrida.
 *
 * Si la lectura FALLA —por ejemplo un miembro sin `canViewAnalytics`— la tarjeta
 * no se muestra. Un error no puede terminar en un número: sería la tercera vez
 * en este bloque que un silencio se disfraza de cero.
 */

import React, { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import { db } from '../../firebase';
import { formatRelativeTime } from '../../utils/alertHelpers';
import {
  RANGOS, diaEnZona, zonaDeLosDocs, resumirRango,
  conversionPorMonto, estadoDeVentas, ultimaActualizacion, nombreDeCanalUnico,
} from '../../utils/ventasResumen';

export default function VentasCard({ uid, organizationId, integrations }) {
  const { t } = useTranslation('dashboard');
  const [docs, setDocs] = useState(null);   // null = cargando · [] = sin mediciones
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!uid) return;
    let vivo = true;

    (async () => {
      try {
        const base = organizationId ? `organizations/${organizationId}` : `clientes/${uid}`;
        const snap = await getDocs(collection(db, `${base}/metrics/ecommerce/daily`));
        if (vivo) setDocs(snap.docs.map((d) => d.data()));
      } catch (e) {
        // Permiso denegado o red caída. NO se cae a lista vacía: eso mostraría
        // "todavía no hay mediciones", que es una afirmación que no podemos hacer.
        console.error('[VentasCard] no se pudieron leer las métricas:', e);
        if (vivo) setError(true);
      }
    })();

    return () => { vivo = false; };
  }, [uid, organizationId]);

  if (error || docs === null) return null;

  const zona = zonaDeLosDocs(docs);
  const hoy = diaEnZona(new Date(), zona);
  const estado = estadoDeVentas(docs);
  const sello = ultimaActualizacion(docs);
  const canal = nombreDeCanalUnico(integrations);

  const dinero = (n, moneda) => new Intl.NumberFormat('es', {
    style: 'currency', currency: moneda || 'CLP', maximumFractionDigits: 0,
  }).format(n);

  return (
    <div className="ventas-card">
      <h3 className="ventas-card-titulo">{t('ventas.titulo')}</h3>

      {estado === 'sin-mediciones' ? (
        // El único estado que no puede apoyarse en el sello: no hay nada que
        // sellar. Desaparece con la primera corrida y no vuelve.
        <p className="ventas-card-vacio">{t('ventas.sinMediciones')}</p>
      ) : (
        <>
          <div className="ventas-card-rangos">
            {RANGOS.map((rango) => {
              const r = resumirRango(docs, rango, hoy);
              return (
                <div className="ventas-rango" key={rango}>
                  <span className="ventas-rango-nombre">{t(`ventas.rango.${rango}`)}</span>
                  <span className="ventas-rango-ventas">
                    {r.ventas === 0
                      ? t('ventas.sinVentas')
                      : t('ventas.ventas', { count: r.ventas })}
                  </span>
                  <span className="ventas-rango-monto">
                    {r.ventas === 0 ? '' : dinero(r.monto, r.moneda)}
                  </span>
                </div>
              );
            })}
          </div>

          {/* LA CONCLUSIÓN VA DEBAJO DE LOS NÚMEROS, no arriba: quien abre esto a
              las 9 de la mañana quiere saber cuánto vendió hoy.

              QUÉ SIGNIFICA Y QUÉ NO — LEER ANTES DE CAMBIAR LA REDACCIÓN.

              "Salieron de una conversación" NO es "los causó el bot". Lo único
              que el dato respalda es que el link de pago se generó dentro de un
              chat: el pedido llegó con el ancla `pux_chatId` puesta. No dice que
              sin el bot esa venta no habría ocurrido — el cliente podía estar
              decidido y usar el chat sólo para pagar.

              Por eso la frase describe el RECORRIDO y no la causa. Si alguna vez
              se propone cambiarla por "el bot te generó $208.250", eso es una
              promesa de causalidad que la atribución no puede sostener, y el
              primer cliente que compare contra su propia intuición del negocio va
              a dejar de creerle al tablero entero.

              DE DÓNDE SALE EL CANAL. De `integrations` —los canales de
              mensajería activos del dashboard— y NO de `channelBreakdown` del
              agregado, que se arma con `pux_source`, hoy la constante literal
              `'chat'` (`toolRouter.js`). Se nombra sólo si hay UN canal activo;
              con varios queda genérica, que es lo correcto: no se sabe de cuál
              salió la venta sin mirar pedido por pedido. */}
          {(() => {
            const c = conversionPorMonto(docs, hoy);
            if (!c) return null;
            return (
              <p className="ventas-card-conversion">
                {canal
                  ? t('ventas.conversionConCanal', { pesos: c.deCadaCien, canal })
                  : t('ventas.conversion', { pesos: c.deCadaCien })}
              </p>
            );
          })()}

          <p className="ventas-card-pie">
            {sello && t('ventas.actualizado', { cuando: formatRelativeTime(sello) })}
            {sello && ' · '}
            {(() => {
              const m = resumirRango(docs, 'mes', hoy);
              return t('ventas.diasMedidos', { medidos: m.diasMedidos, total: m.diasDelRango });
            })()}
          </p>
        </>
      )}
    </div>
  );
}
